import { useEffect, useState, useRef, useCallback } from 'react';
import { ImagePriority, getRootMargin } from '@/utils/imagePriority';

interface UseInViewOptions {
  priority: ImagePriority;
  threshold?: number;
  root?: Element | null;
}

interface UseInViewReturn {
  ref: React.RefObject<any>;
  isInView: boolean;
  shouldLoad: boolean;
}

/**
 * Custom hook for intersection observer with priority-based loading
 */
export function useInView({ priority, threshold = 0.1, root = null }: UseInViewOptions): UseInViewReturn {
  const [isInView, setIsInView] = useState(false);
  const [shouldLoad, setShouldLoad] = useState(false);
  const ref = useRef<any>(null);

  const rootMargin = getRootMargin(priority);

  const handleIntersection = useCallback((entries: IntersectionObserverEntry[]) => {
    const [entry] = entries;
    
    if (entry.isIntersecting) {
      setIsInView(true);
      setShouldLoad(true);
    }
  }, []);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    // High priority images load immediately
    if (priority === 'high') {
      setIsInView(true);
      setShouldLoad(true);
      return;
    }

    const observer = new IntersectionObserver(handleIntersection, {
      threshold,
      root,
      rootMargin,
    });

    observer.observe(element);

    return () => {
      observer.unobserve(element);
    };
  }, [priority, threshold, root, rootMargin, handleIntersection]);

  return { ref, isInView, shouldLoad };
}

/**
 * Enhanced hook with window resize handling for responsive priority
 */
export function useInViewWithPriority(
  priority: ImagePriority,
  options: Omit<UseInViewOptions, 'priority'> = {}
): UseInViewReturn {
  const [windowWidth, setWindowWidth] = useState<number>(1024);

  useEffect(() => {
    const updateWindowWidth = () => {
      setWindowWidth(window.innerWidth);
    };

    // Set initial width
    updateWindowWidth();

    window.addEventListener('resize', updateWindowWidth);
    return () => window.removeEventListener('resize', updateWindowWidth);
  }, []);

  return useInView({ priority, ...options });
}
